/**
 * PlatformBreakGlass — admin console tab for break-glass access.
 *
 * Shows the current active session (if any), a form to start a new one,
 * and a full history table with revoke action. Styled to match the
 * Platform console's amber-on-dark theme.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { breakGlassAPI, platformAPI, setBreakGlassSessionId } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Card, CardContent } from '../ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { AlertOctagon, Play, XCircle, Clock, Search, Download } from 'lucide-react';

const DURATION_OPTIONS = [
  { v: 15, label: '15 minutes' },
  { v: 30, label: '30 minutes' },
  { v: 60, label: '60 minutes (default)' },
  { v: 120, label: '2 hours' },
  { v: 240, label: '4 hours (max)' },
];

const STATUS_STYLES = {
  active: 'border-red-500/40 text-red-300 bg-red-500/10',
  ended: 'border-zinc-600 text-zinc-300',
  expired: 'border-zinc-700 text-zinc-500',
  revoked: 'border-amber-500/40 text-amber-300 bg-amber-500/10',
  superseded: 'border-zinc-700 text-zinc-500',
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function PlatformBreakGlass() {
  const [active, setActive] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStart, setShowStart] = useState(false);
  const [orgQuery, setOrgQuery] = useState('');
  const [form, setForm] = useState({ target_organisation_id: '', reason: '', duration_minutes: 60, mfa_code: '' });
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [a, s] = await Promise.all([
        breakGlassAPI.active(),
        breakGlassAPI.list({ limit: 100 }),
      ]);
      setActive(a.data || null);
      setSessions(s.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load break-glass sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Load org list once for the picker. Uses the metadata-only endpoint.
  const loadOrgs = useCallback(async () => {
    try {
      const r = await platformAPI.listOrganisations({ limit: 200 });
      setOrgs(r.data?.items || []);
    } catch {
      /* non-fatal — admin can type the org id manually */
    }
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const filteredOrgs = orgs.filter((o) => {
    if (!orgQuery) return true;
    const q = orgQuery.toLowerCase();
    return (o.name || '').toLowerCase().includes(q) || (o.slug || '').toLowerCase().includes(q);
  });

  const handleStart = async (e) => {
    e.preventDefault();
    if (!form.target_organisation_id) { toast.error('Pick a target organisation'); return; }
    if ((form.reason || '').trim().length < 20) {
      toast.error('Reason must be at least 20 characters');
      return;
    }
    if (!form.mfa_code || form.mfa_code.trim().length < 6) {
      toast.error('Enter the 6-digit code from your authenticator');
      return;
    }
    setSubmitting(true);
    try {
      const res = await breakGlassAPI.start({ ...form, mfa_code: form.mfa_code.trim() });
      setBreakGlassSessionId(res.data?.id);
      toast.success(`Support session started · ${res.data?.target_organisation_name}`);
      setShowStart(false);
      setForm({ target_organisation_id: '', reason: '', duration_minutes: 60, mfa_code: '' });
      setOrgQuery('');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnd = async () => {
    if (!window.confirm('End your current support session?')) return;
    try {
      await breakGlassAPI.end();
      setBreakGlassSessionId(null);
      toast.success('Support session ended');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to end session');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Force-end this session? The acting admin will lose access immediately.')) return;
    try {
      await breakGlassAPI.revoke(id);
      toast.success('Session revoked');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke');
    }
  };

  return (
    <div className="space-y-6" data-testid="platform-break-glass">
      {/* Active banner summary */}
      <Card
        className="border"
        style={{ background: active ? '#2a0f0f' : '#17171E', borderColor: active ? '#5b1d1d' : '#27272A' }}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <AlertOctagon className={`h-5 w-5 mt-0.5 ${active ? 'text-red-400 animate-pulse' : 'text-zinc-500'}`} />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100">
                  {active ? 'Support session active' : 'No active support session'}
                </div>
                {active ? (
                  <div className="text-xs text-zinc-400 mt-0.5 space-y-0.5">
                    <div>Target: <span className="text-zinc-200 font-medium">{active.target_organisation_name}</span></div>
                    <div>Reason: <span className="text-zinc-300">{active.reason}</span></div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Expires {fmtDate(active.expires_at)} · {active.request_count} request{active.request_count === 1 ? '' : 's'} · {active.write_count} write{active.write_count === 1 ? '' : 's'}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Start a time-boxed session to access a tenant's data for support. All actions audited.
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {active ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-500/40 text-red-300 hover:bg-red-500/20"
                  onClick={handleEnd}
                  data-testid="break-glass-end-active-btn"
                >
                  <XCircle className="w-4 h-4 mr-1" /> End session
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                  onClick={() => setShowStart(true)}
                  data-testid="break-glass-start-btn"
                >
                  <Play className="w-4 h-4 mr-1" /> Start support session
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="border" style={{ background: '#17171E', borderColor: '#27272A' }}>
        <CardContent className="p-5">
          <div className="text-sm font-semibold text-zinc-100 mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" /> Session history
            </span>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              onClick={async () => {
                try {
                  // Hit the CSV endpoint via axios then stream to the browser.
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/platform/break-glass/export/csv?include_actions=true&limit=1000`,
                    { credentials: 'include' }
                  );
                  if (!res.ok) throw new Error(await res.text());
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `break-glass-audit-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.csv`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success('Evidence pack downloaded');
                } catch (e) {
                  toast.error('Could not export CSV');
                }
              }}
              data-testid="break-glass-export-csv-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Export CSV
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400">Admin</TableHead>
                <TableHead className="text-zinc-400">Target org</TableHead>
                <TableHead className="text-zinc-400">Reason</TableHead>
                <TableHead className="text-zinc-400">Started</TableHead>
                <TableHead className="text-zinc-400">Ended</TableHead>
                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                <TableHead className="text-zinc-400 text-right">Writes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-6">Loading…</TableCell></TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-zinc-500 py-6" data-testid="no-sessions-empty">
                    No support sessions yet.
                  </TableCell>
                </TableRow>
              ) : sessions.map((s) => (
                <TableRow key={s.id} className="border-zinc-800 hover:bg-zinc-900/50" data-testid={`break-glass-row-${s.id}`}>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLES[s.status] || ''}>
                      {s.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-200 text-sm">{s.platform_admin_name || s.platform_admin_email}</TableCell>
                  <TableCell className="text-zinc-200 text-sm">{s.target_organisation_name}</TableCell>
                  <TableCell className="text-zinc-400 text-xs max-w-xs truncate" title={s.reason}>{s.reason}</TableCell>
                  <TableCell className="text-zinc-400 text-xs">{fmtDate(s.started_at)}</TableCell>
                  <TableCell className="text-zinc-400 text-xs">{fmtDate(s.ended_at)}</TableCell>
                  <TableCell className="text-right text-zinc-300 text-sm tabular-nums">{s.request_count}</TableCell>
                  <TableCell className="text-right text-zinc-300 text-sm tabular-nums">
                    {s.write_count}
                    {s.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 text-red-400 hover:bg-red-500/10 h-7"
                        onClick={() => handleRevoke(s.id)}
                        data-testid={`break-glass-revoke-${s.id}`}
                      >
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Start dialog */}
      <Dialog open={showStart} onOpenChange={setShowStart}>
        <DialogContent className="bg-[#17171E] border-zinc-800 text-zinc-100" aria-describedby="bg-dialog-desc">
          <DialogHeader>
            <DialogTitle>Start support session</DialogTitle>
            <DialogDescription id="bg-dialog-desc" className="text-zinc-500">
              You'll be scoped to the target organisation's data until you end the session or it auto-expires.
              Every action is audited against this record.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStart} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-200">Target organisation</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={orgQuery}
                  onChange={(e) => setOrgQuery(e.target.value)}
                  placeholder="Search organisations…"
                  className="pl-9 bg-[#0F0F15] border-zinc-800 text-zinc-100"
                  data-testid="break-glass-org-search"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border border-zinc-800 rounded">
                {filteredOrgs.length === 0 ? (
                  <div className="text-xs text-zinc-500 p-3">No matches.</div>
                ) : filteredOrgs.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, target_organisation_id: o.id }))}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-800 border-b border-zinc-800 last:border-0 ${
                      form.target_organisation_id === o.id ? 'bg-amber-500/10 text-amber-200' : 'text-zinc-200'
                    }`}
                    data-testid={`break-glass-org-option-${o.id}`}
                  >
                    <div className="font-medium">{o.name}</div>
                    <div className="text-xs text-zinc-500">{o.slug} · {o.status}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-200">Reason (minimum 20 characters)</Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Customer reported login issue in ticket #4821 — needs admin to inspect their auth events."
                className="bg-[#0F0F15] border-zinc-800 text-zinc-100 min-h-24"
                data-testid="break-glass-reason-input"
              />
              <div className="text-xs text-zinc-500 flex justify-between">
                <span>Captured on the audit record forever.</span>
                <span className={form.reason.trim().length >= 20 ? 'text-zinc-400' : 'text-amber-400'}>
                  {form.reason.trim().length}/20
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-200">Duration</Label>
              <Select
                value={String(form.duration_minutes)}
                onValueChange={(v) => setForm((f) => ({ ...f, duration_minutes: Number(v) }))}
              >
                <SelectTrigger className="bg-[#0F0F15] border-zinc-800 text-zinc-100" data-testid="break-glass-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#17171E] border-zinc-800">
                  {DURATION_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={String(o.v)} className="text-zinc-200">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-200">Verification code (from your authenticator)</Label>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={11}
                value={form.mfa_code}
                onChange={(e) => setForm((f) => ({ ...f, mfa_code: e.target.value }))}
                placeholder="123456"
                className="bg-[#0F0F15] border-zinc-800 text-zinc-100 text-lg tracking-widest text-center"
                data-testid="break-glass-mfa-input"
              />
              <div className="text-xs text-zinc-500">
                A fresh 6-digit code is required at the exact moment the session is opened.
                Backup codes also work.
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowStart(false)} className="text-zinc-400">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                disabled={submitting || !form.target_organisation_id || form.reason.trim().length < 20 || !form.mfa_code}
                data-testid="break-glass-dialog-submit"
              >
                {submitting ? 'Starting…' : (<><Play className="w-4 h-4 mr-1" />Start session</>)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
